/**
 * Firestore REST API utility — bypasses the broken Firestore SDK.
 * All reads and writes go through the REST API which works reliably.
 */

import * as Sentry from '@sentry/react'
import { auth } from './firebase'

const PROJECT_ID = 'desres-kitchen-v2'
const REST_BASE = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`

// Capture and rethrow — ensures every REST failure reaches Sentry
function captureAndThrow(message, extra = {}) {
  const err = new Error(message)
  Sentry.captureException(err, { extra })
  throw err
}

// --- Parse Firestore REST response fields → plain JS ---

function parseValue(v) {
  if (!v) return null
  if ('booleanValue' in v) return v.booleanValue
  if ('doubleValue' in v) return Number(v.doubleValue)
  if ('integerValue' in v) return Number(v.integerValue)
  if ('stringValue' in v) return v.stringValue
  if ('nullValue' in v) return null
  if ('arrayValue' in v) return (v.arrayValue.values || []).map(parseValue)
  if ('mapValue' in v) return fromFields(v.mapValue.fields)
  return v
}

function fromFields(fields) {
  if (!fields) return {}
  const obj = {}
  for (const [k, v] of Object.entries(fields)) {
    obj[k] = parseValue(v)
  }
  return obj
}

// --- Convert plain JS → Firestore REST fields ---

function toValue(v) {
  if (v === null || v === undefined) return { nullValue: null }
  if (typeof v === 'boolean') return { booleanValue: v }
  if (typeof v === 'number') {
    if (Number.isNaN(v)) return { integerValue: '0' } // NaN safety — Firestore rejects NaN
    return Number.isInteger(v) ? { integerValue: String(v) } : { doubleValue: v }
  }
  if (typeof v === 'string') return { stringValue: v }
  if (Array.isArray(v)) return { arrayValue: { values: v.map(toValue) } }
  if (typeof v === 'object') return { mapValue: { fields: toFields(v) } }
  return { stringValue: String(v) }
}

function toFields(obj) {
  const fields = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) fields[k] = toValue(v)
  }
  return fields
}

// --- Auth helper ---

import { onAuthStateChanged } from 'firebase/auth'

/** Wait for Firebase Auth to initialize and return the current user (or null).
 *  Distinguishes "still loading" vs "not logged in" — fires once onAuthStateChanged resolves. */
let _authReady = null
export function waitForAuth() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser)
  if (_authReady) return _authReady
  _authReady = new Promise(resolve => {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub() // prevent memory leaks
      _authReady = null
      resolve(user)
    })
  })
  return _authReady
}

/** Get a valid auth token, or null if not logged in */
export async function getAuthTokenOrNull() {
  const user = await waitForAuth()
  return user ? await user.getIdToken() : null
}

async function getHeaders(requireAuth = false) {
  const headers = { 'Content-Type': 'application/json' }
  // Wait for auth to initialize if currentUser is not yet available
  let user = auth.currentUser
  if (!user) user = await waitForAuth()
  const token = user ? await user.getIdToken() : null
  if (token) headers['Authorization'] = `Bearer ${token}`
  else if (requireAuth) throw new Error('Not authenticated')
  return headers
}

// --- 401 retry helper ---

async function fetchWithRetry(url, options, requireAuth = false) {
  let res = await fetch(url, options)
  if (res.status === 401 && auth.currentUser) {
    // Token expired — force refresh and retry once
    try {
      const newToken = await auth.currentUser.getIdToken(true)
      const newHeaders = { ...options.headers, 'Authorization': `Bearer ${newToken}` }
      res = await fetch(url, { ...options, headers: newHeaders })
    } catch {
      // Refresh failed — session expired
    }
  }
  return res
}

// --- CRUD operations ---

/** Read a single document */
export async function restGet(path) {
  const headers = await getHeaders()
  const res = await fetchWithRetry(`${REST_BASE}/${path}`, { headers })
  if (res.status === 404) return null
  if (!res.ok) captureAndThrow(`REST GET ${path}: ${res.status}`, { path, method: 'GET' })
  const data = await res.json()
  return fromFields(data.fields)
}

/** Write/update a document (PATCH = create or overwrite) */
export async function restSet(path, obj) {
  const headers = await getHeaders(true)
  const res = await fetchWithRetry(`${REST_BASE}/${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ fields: toFields(obj) }),
  })
  if (!res.ok) {
    const err = await res.text()
    captureAndThrow(`REST SET ${path}: ${res.status} ${err}`, { path, method: 'PATCH' })
  }
  return true
}

/** Delete a document */
export async function restDelete(path) {
  const headers = await getHeaders(true)
  const res = await fetchWithRetry(`${REST_BASE}/${path}`, { method: 'DELETE', headers })
  if (!res.ok && res.status !== 404) {
    captureAndThrow(`REST DELETE ${path}: ${res.status}`, { path, method: 'DELETE' })
  }
  return true
}

/** List all documents in a collection */
export async function restList(collectionPath) {
  const headers = await getHeaders()
  const results = []
  let pageToken = null
  do {
    const url = new URL(`${REST_BASE}/${collectionPath}`)
    url.searchParams.set('pageSize', '300')
    if (pageToken) url.searchParams.set('pageToken', pageToken)
    const res = await fetch(url.toString(), { headers })
    if (!res.ok) captureAndThrow(`REST LIST ${collectionPath}: ${res.status}`, { collection: collectionPath, method: 'LIST' })
    const data = await res.json()
    for (const doc of (data.documents || [])) {
      const id = doc.name.split('/').pop()
      results.push({ id, ...fromFields(doc.fields) })
    }
    pageToken = data.nextPageToken
  } while (pageToken)
  return results
}

/** Query documents with a field filter (equivalent to where(field, '==', value)) */
export async function restQuery(collectionPath, field, value) {
  const headers = await getHeaders()
  const body = {
    structuredQuery: {
      from: [{ collectionId: collectionPath }],
      where: {
        fieldFilter: {
          field: { fieldPath: field },
          op: 'EQUAL',
          value: toValue(value),
        },
      },
    },
  }
  const parentPath = `projects/${PROJECT_ID}/databases/(default)/documents`
  const res = await fetch(
    `https://firestore.googleapis.com/v1/${parentPath}:runQuery`,
    { method: 'POST', headers, body: JSON.stringify(body) }
  )
  if (!res.ok) captureAndThrow(`REST QUERY ${collectionPath} where ${field}==${value}: ${res.status}`, { collection: collectionPath, field, value, method: 'QUERY' })
  const data = await res.json()
  const results = []
  for (const item of data) {
    if (item.document) {
      const id = item.document.name.split('/').pop()
      results.push({ id, ...fromFields(item.document.fields) })
    }
  }
  return results
}

/** Delete all documents matching a field filter */
export async function restDeleteByQuery(collectionPath, field, value, extraFilter) {
  const docs = await restQuery(collectionPath, field, value)
  const toDelete = extraFilter ? docs.filter(extraFilter) : docs
  const headers = await getHeaders(true)
  let deleted = 0
  for (let i = 0; i < toDelete.length; i += 10) {
    const batch = toDelete.slice(i, i + 10)
    await Promise.all(batch.map(d =>
      fetch(`${REST_BASE}/${collectionPath}/${d.id}`, { method: 'DELETE', headers })
        .then(r => { if (r.ok || r.status === 404) deleted++ })
        .catch(() => {})
    ))
  }
  return deleted
}

/** Deduplicate documents: for each group defined by keyFn, keep only the newest */
export async function restDedup(collectionPath, field, value, keyFn) {
  const docs = await restQuery(collectionPath, field, value)
  if (docs.length === 0) return 0
  const groups = {}
  for (const d of docs) {
    const k = keyFn(d)
    if (!groups[k]) groups[k] = []
    groups[k].push(d)
  }
  const toDelete = []
  for (const records of Object.values(groups)) {
    if (records.length <= 1) continue
    // Sort by createdAt descending, keep the newest
    records.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    toDelete.push(...records.slice(1))
  }
  if (toDelete.length === 0) return 0
  const headers = await getHeaders(true)
  let deleted = 0
  for (let i = 0; i < toDelete.length; i += 10) {
    const batch = toDelete.slice(i, i + 10)
    await Promise.all(batch.map(d =>
      fetch(`${REST_BASE}/${collectionPath}/${d.id}`, { method: 'DELETE', headers })
        .then(r => { if (r.ok || r.status === 404) deleted++ })
        .catch(() => {})
    ))
  }
  return deleted
}

/** Batch write multiple documents (each as a separate PATCH — Firestore REST doesn't have true batch) */
export async function restBatchSet(collectionPath, docs) {
  const headers = await getHeaders(true)
  const results = []
  // Process in parallel batches of 10
  for (let i = 0; i < docs.length; i += 10) {
    const batch = docs.slice(i, i + 10)
    const promises = batch.map(({ id, ...data }) =>
      fetch(`${REST_BASE}/${collectionPath}/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ fields: toFields(data) }),
      }).then(r => ({ id, ok: r.ok, status: r.status }))
    )
    results.push(...await Promise.all(promises))
  }
  return results
}
